import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, MailQuestion, Unlink } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "@/components/ui/sonner";
import { ApiTokensPanel } from "@/components/ApiTokensPanel";
import { ApiTokenLogsPanel } from "@/components/ApiTokenLogsPanel";

const ROLES = ["admin", "export", "gt_export", "buyer", "production_manager", "sales_manager", "procurement_manager", "market_surveyor", "product_manager", "user_admin", "factory", "customer"];
const LANGUAGES = [["zh", "简体中文"], ["en", "English"]];
const CURRENCIES = ["USD", "CAD"];
const EMPTY_FORM = { name: "", email: "", password: "", role: "export", language: "zh", currency: "USD", price_multiplier: 1.0, manager_email: "" };

const RoleSelect = ({ value, onChange, t, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      {ROLES.map((r) => <SelectItem key={r} value={r}>{t(`users.role_${r}`)}</SelectItem>)}
    </SelectContent>
  </Select>
);

// Manager (上级) — dropdown of sales_manager users (2026-07). Empty option = no manager.
const ManagerSelect = ({ value, onChange, managers, t, testId }) => (
  <Select value={value || "__none__"} onValueChange={(v) => onChange(v === "__none__" ? "" : v)}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder={t("users.managerNone")} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="__none__">{t("users.managerNone")}</SelectItem>
      {managers.map((m) => (
        <SelectItem key={m.email} value={m.email}>{m.name || m.email}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const LanguageSelect = ({ value, onChange, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      {LANGUAGES.map(([code, label]) => <SelectItem key={code} value={code}>{label}</SelectItem>)}
    </SelectContent>
  </Select>
);

const CurrencySelect = ({ value, onChange, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function Users() {
  const { user: currentUser } = useAuth();
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // user being edited
  // 2026-07 · list of manager users, used by the "上级" dropdown so
  // employees can be linked to their direct manager. 2026-02-09 · dropdown
  // is now role-aware: buyers pick a procurement_manager (采购经理); everyone
  // else (export, gt_export, sales_manager themselves, etc.) picks a
  // sales_manager. This prevents "wrong department" mismatches.
  const managersFor = (role) => users.filter((u) => (
    role === "buyer" ? u.role === "procurement_manager" : u.role === "sales_manager"
  ));

  const fetchUsers = async () => {
    try {
      const { data } = await api.get("/auth/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreate = async () => {
    setSaving(true);
    try {
      await api.post("/auth/register", form);
      toast.success(t("common.save"));
      setForm(EMPTY_FORM);
      setOpen(false);
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      // 2026-10-25 · 密码只在用户敲了新值时才带上；留空代表不改密码。
      const payload = {
        name: editing.name, email: editing.email, role: editing.role,
        language: editing.language || "zh",
        currency: editing.currency || "USD",
        price_multiplier: Number(editing.price_multiplier) > 0 ? Number(editing.price_multiplier) : 1.0,
        manager_email: (editing.manager_email || "").toLowerCase(),
      };
      const newPw = (editing.new_password || "").trim();
      if (newPw) {
        if (newPw.length < 8 || !/[A-Za-z]/.test(newPw) || !/\d/.test(newPw)) {
          toast.error(t("users.passwordRule") || "密码至少 8 位，必须包含字母和数字");
          setSaving(false);
          return;
        }
        payload.password = newPw;
      }
      await api.put(`/auth/users/${editing._id}`, payload);
      toast.success(t("common.save"));
      setEditing(null);
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/auth/users/${id}`);
      toast.success("Deleted");
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleUnbindDevices = async (u) => {
    try {
      await api.post(`/auth/users/${u._id}/unbind-devices`);
      toast.success(t("users.devicesUnbound"));
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleSendReset = async (email) => {
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success(`${t("reset.emailSent")}: ${email}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="animate-fade-in-up">
      <Tabs defaultValue="users" className="space-y-6">
        <TabsList data-testid="users-page-tabs">
          <TabsTrigger value="users" data-testid="tab-users">{t("users.title")}</TabsTrigger>
          <TabsTrigger value="api-tokens" data-testid="tab-api-tokens">{t("apiTokens.title")}</TabsTrigger>
          <TabsTrigger value="api-logs" data-testid="tab-api-logs">{t("apiTokens.logs")}</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-6">
      <div className="flex items-center justify-between mb-3">
        <h1 className="font-heading text-base font-bold tracking-tight text-foreground">{t("users.title")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button" className="rounded-md bg-brand hover:bg-brand-hover transition-colors">
              <Plus size={16} /> {t("users.addEmployee")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("users.addEmployee")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>{t("users.name")}</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("users.email")}</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("users.password")}</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("users.role")}</Label>
                <RoleSelect value={form.role} onChange={(v) => setForm({ ...form, role: v })} t={t} testId="new-user-role-select" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("users.manager")}</Label>
                <ManagerSelect
                  value={form.manager_email || ""}
                  onChange={(v) => setForm({ ...form, manager_email: v })}
                  managers={managersFor(form.role)}
                  t={t}
                  testId="new-user-manager-select"
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t("users.language")}</Label>
                <LanguageSelect value={form.language} onChange={(v) => setForm({ ...form, language: v })} testId="new-user-language-select" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>{t("users.currency")}</Label>
                  <CurrencySelect value={form.currency} onChange={(v) => setForm({ ...form, currency: v })} testId="new-user-currency-select" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("users.priceMultiplier")}</Label>
                  <Input
                    type="number" step="0.01" min="0.01" value={form.price_multiplier}
                    onChange={(e) => setForm({ ...form, price_multiplier: e.target.value })}
                    data-testid="new-user-price-multiplier-input"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleCreate} disabled={saving} data-testid="submit-new-user-button" className="rounded-md bg-brand hover:bg-brand-hover">
                {saving ? "..." : t("common.save")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* edit user dialog */}
      <Dialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-sm" data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle>{t("users.editUser")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{t("users.name")}</Label>
              <Input value={editing?.name || ""} onChange={(e) => setEditing((u) => ({ ...u, name: e.target.value }))} data-testid="edit-user-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("users.email")}</Label>
              <Input type="email" value={editing?.email || ""} onChange={(e) => setEditing((u) => ({ ...u, email: e.target.value }))} data-testid="edit-user-email-input" />
            </div>
            {/* 2026-10-25 · 编辑密码 —— 留空不改。guard_admin_target 保证不能改管理员密码。 */}
            <div className="space-y-1.5">
              <Label>
                {t("users.passwordNew") || "新密码"}
                <span className="ml-1.5 text-[10px] text-muted-foreground font-normal">
                  ({t("users.passwordHint") || "留空不修改；至少 8 位、含字母和数字"})
                </span>
              </Label>
              <Input
                type="password"
                autoComplete="new-password"
                placeholder={t("users.passwordLeaveBlank") || "留空保持当前密码"}
                value={editing?.new_password || ""}
                onChange={(e) => setEditing((u) => ({ ...u, new_password: e.target.value }))}
                data-testid="edit-user-password-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("users.role")}</Label>
              <RoleSelect value={editing?.role || "export"} onChange={(v) => setEditing((u) => ({ ...u, role: v }))} t={t} testId="edit-user-role-select" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("users.manager")}</Label>
              <ManagerSelect
                value={editing?.manager_email || ""}
                onChange={(v) => setEditing((u) => ({ ...u, manager_email: v }))}
                managers={managersFor(editing?.role)}
                t={t}
                testId="edit-user-manager-select"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("users.language")}</Label>
              <LanguageSelect value={editing?.language || "zh"} onChange={(v) => setEditing((u) => ({ ...u, language: v }))} testId="edit-user-language-select" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("users.currency")}</Label>
                <CurrencySelect value={editing?.currency || "USD"} onChange={(v) => setEditing((u) => ({ ...u, currency: v }))} testId="edit-user-currency-select" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("users.priceMultiplier")}</Label>
                <Input
                  type="number" step="0.01" min="0.01" value={editing?.price_multiplier ?? 1.0}
                  onChange={(e) => setEditing((u) => ({ ...u, price_multiplier: e.target.value }))}
                  data-testid="edit-user-price-multiplier-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-md">{t("common.cancel")}</Button>
            <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-user-edit-button" className="rounded-md bg-brand hover:bg-brand-hover">
              {saving ? "..." : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="bg-card border border-border rounded-md overflow-hidden">
        <Table data-testid="users-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t("users.name")}</TableHead>
              <TableHead>{t("users.email")}</TableHead>
              <TableHead>{t("users.role")}</TableHead>
              <TableHead>{t("users.language")}</TableHead>
              <TableHead className="text-center">{t("users.currency")}</TableHead>
              <TableHead className="text-center">{t("users.priceMultiplier")}</TableHead>
              <TableHead className="text-center">{t("users.devices")}</TableHead>
              <TableHead className="text-right">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u._id} data-testid={`user-row-${u._id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <Badge variant={u.role === "admin" ? "default" : "secondary"} data-testid={`user-role-${u._id}`}>
                    {t(`users.role_${u.role}`) || u.role}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span className="text-xs font-mono" data-testid={`user-language-${u._id}`}>
                    {u.language === "en" ? "English" : "中文"}
                  </span>
                </TableCell>
                <TableCell className="text-center">
                  <span className="text-xs font-mono font-semibold" data-testid={`user-currency-${u._id}`}>
                    {u.currency || "USD"}
                  </span>
                </TableCell>
                <TableCell className="text-center">
                  <span className="text-xs font-mono tabular-nums" data-testid={`user-price-multiplier-${u._id}`}>
                    ×{Number(u.price_multiplier ?? 1).toFixed(2)}
                  </span>
                </TableCell>
                {/* 2026-06 · 设备绑定 — bound device count + one-click unbind */}
                <TableCell className="text-center">
                  {u.role === "admin" ? (
                    <span className="text-xs text-muted-foreground" data-testid={`user-devices-${u._id}`}>—</span>
                  ) : (
                    <div className="flex items-center justify-center gap-1">
                      <span
                        className="text-xs font-mono tabular-nums"
                        title={(u.devices || []).map((d) => d.label).join(" / ")}
                        data-testid={`user-devices-${u._id}`}
                      >
                        {(u.devices || []).length}/3
                      </span>
                      {(u.devices || []).length > 0 && (
                        <Button
                          variant="ghost" size="icon" className="h-7 w-7"
                          onClick={() => handleUnbindDevices(u)}
                          title={t("users.unbindDevices")}
                          data-testid={`unbind-devices-${u._id}`}
                        >
                          <Unlink size={13} />
                        </Button>
                      )}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => handleSendReset(u.email)}
                      title={t("users.sendReset")}
                      data-testid={`send-reset-${u._id}`}
                    >
                      <MailQuestion size={15} />
                    </Button>
                    {/* 2026-06 · 用户管理员不能编辑/删除管理员账号 */}
                    <Button
                      variant="ghost" size="icon"
                      disabled={currentUser?.role === "user_admin" && u.role === "admin"}
                      onClick={() => setEditing({ ...u })}
                      title={t("common.edit")}
                      data-testid={`edit-user-${u._id}`}
                    >
                      <Pencil size={15} />
                    </Button>
                    {u._id !== currentUser?.id && !(currentUser?.role === "user_admin" && u.role === "admin") && (
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(u._id)} data-testid={`delete-user-${u._id}`}>
                        <Trash2 size={16} className="text-destructive" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
        </TabsContent>

        <TabsContent value="api-tokens">
          <ApiTokensPanel />
        </TabsContent>

        <TabsContent value="api-logs">
          <ApiTokenLogsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
